import React, { useState } from 'react';
import { Box, Checkbox, IconButton, Icon } from '@mui/material';
import { FlexGridCell, FlexGridCheckboxCell, FlexGridRow } from 'global/components/flex-grid-list';
import { get, set, size } from 'global/lib/lodash';
import { getAvg, percentify, stopProp } from 'global/utils';
import moment from 'moment';
import { useScreen } from 'global/styles';

const colFlexGrow = (title) => (title === 'Name' || title === 'Course Assignments' ? 2 : 1);

const getValueForCompletion = (r, show0 = true) => {
  const val = r._userAssignments ? getAvg(r._userAssignments.filter((ua) => ua.completion != null).map((ua) => ua.completion)) : r.completion;
  if (val === null || val < 0 || isNaN(val)) {
    return show0 ? percentify(0) : 'Not Started';
  } else {
    return percentify(val || 0);
  }
};

const getValueForScore = (r, defaultToNa = false) => {
  const val = r._userAssignments ? getAvg(r._userAssignments.filter((ua) => ua.score != null).map((ua) => ua.score)) : r.score;

  const scorecardId = r.scorecardId !== undefined ? r.scorecardId : get(r, 'assignments', []).find((f) => f.scorecardId !== null);

  if (val === null || isNaN(val) || val < 0) {
    return scorecardId === null || scorecardId === undefined ? 'N/A' : defaultToNa ? 'N/A' : 'Not Started';
  } else if (val < 0) {
    return defaultToNa ? 'N/A' : 'Not Started';
  } else {
    return percentify(val || 0);
  }
};

const getCols = (filter, opts = {}) => {
  if (filter.report_type === 'user') {
    return {
      showCheckbox: true,
      cols: [
        { title: 'Name', val: (r) => get(r, 'reportable.name') },
        {
          title: 'Course Assignments',
          val: (r) => size(r._userAssignments || r.userAssignments),
          className: 'centerCold',
          dropKey: 'userAssignments',
          getName: (r) => get(r, 'editionName'),
          showDrop: true,
        },
        { title: 'Completion', val: (r) => getValueForCompletion(r, false), dropKey: 'userAssignments', getName: (r) => getValueForCompletion(r, false) },
        !opts.isMobile && { title: 'Quiz Scores', val: (r) => getValueForScore({ ...r, assignments: r._userAssignments || r.userAssignments }), dropKey: 'userAssignments', getName: getValueForScore },
        !opts.isMobile && { title: 'Last Updated', val: (r) => moment(r.updatedAt).fromNow(), getName: (r) => moment(r.updatedAt).fromNow(), dropKey: 'userAssignments' },
      ].filter((f) => !!f),
    };
  } else if (filter.report_type === 'edition') {
    return {
      showCheckbox: false,
      cols: [
        { title: 'Name', val: (r) => [`${get(r, 'reportable.name')}`, `${get(r, 'realm.name')}`], rowClass: 'link' },
        { title: 'Course Assignments', val: (r) => r.count, className: 'centerCold', dropKey: 'editionAssignments' },
        { title: 'Completion', val: getValueForCompletion },
        !opts.isMobile && { title: 'Quiz Scores', val: (r) => getValueForScore({ ...r, assignments: r.editionAssignments }) },
        !opts.isMobile && { title: 'Last Updated', val: (r) => moment(r.updatedAt).fromNow() },
      ].filter((f) => !!f),
    };
  } else if (filter.report_type === 'realm') {
    return {
      showCheckbox: false,
      cols: [
        { title: 'Name', val: (r) => [`${get(r, 'reportable.name')}`, `${get(r, 'realm.name')}`], rowClass: 'link' },
        { title: 'Course Assignments', val: (r) => r.count, className: 'centerCold' },
        { title: 'Completion', val: getValueForCompletion },
        !opts.isMobile && { title: 'Quiz Scores', val: (r) => getValueForScore({ ...r, assignments: r.children }, true) },
        !opts.isMobile && { title: 'Last Updated', val: (r) => moment(r.updatedAt).fromNow() },
      ].filter((f) => !!f),
    };
  } else {
    return { cols: [] };
  }
};

export const RowHeader = (props) => {
  const { filter } = props;
  const { isMobile } = useScreen();

  const COLS = getCols(filter, { isMobile });

  return (
    <FlexGridRow listItem header layout="columns" dense>
      {COLS.showCheckbox && (
        <FlexGridCheckboxCell>
          <Checkbox indeterminate={props.indeterminate} onClick={props.selectAll} checked={props.allSelected} />
        </FlexGridCheckboxCell>
      )}
      {COLS.cols.map((c) => (
        <FlexGridCell key={c.title} flexGrow={colFlexGrow(c.title)}>
          {c.title}
        </FlexGridCell>
      ))}
    </FlexGridRow>
  );
};

export default (props) => {
  const { item, selected, filter } = props;
  const [open, setOpen] = useState(false);
  const { isMobile } = useScreen();

  const COLS = getCols(filter, { isMobile });

  const isSelected = () => {
    return get(selected, item.id) ? true : false;
  };

  const onSelectItem = (e) => {
    stopProp(e);
    props.onSelectItem && props.onSelectItem(props);
  };

  const onSelectRow = (e) => {
    props.onSelectRow && props.onSelectRow(e, props);
  };

  const toggleOpen = (e) => {
    stopProp(e);
    setOpen(!open);
  };

  if (item.userAssignments && props.filtChips) {
    const edFilt = props.filtChips.find((f) => f.filter === 'edition_id');
    if (edFilt) {
      set(
        item,
        '_userAssignments',
        item.userAssignments.filter((u) => u.editionId == get(edFilt, 'value'))
      );
    }
  }

  return (
    <FlexGridRow onClick={onSelectRow} listItem layout="columns" dense clickable alignItems="stretch">
      {COLS.showCheckbox && (
        <FlexGridCheckboxCell>
          <Checkbox onClick={onSelectItem} checked={isSelected()} />
        </FlexGridCheckboxCell>
      )}
      {COLS.cols.map((c) => {
        const value = c.val(item);
        const dropItems = get(item, `_${c.dropKey}`, get(item, c.dropKey, [])) || [];
        return (
          <FlexGridCell
            key={c.title}
            flexGrow={colFlexGrow(c.title)}
            sx={c.rowClass === 'link' ? { color: 'secondary.main' } : undefined}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', height: '100%', minWidth: 0 }}>
              {open && dropItems.length > 1 ? (
                dropItems.map((r) => <div key={r.id}>{c.getName(r)}</div>)
              ) : c.showDrop && dropItems.length == 1 ? (
                <div>{c.getName(dropItems[0])}</div>
              ) : Array.isArray(value) ? (
                value.map((v, i) =>
                  i > 0 ? (
                    <div key={`${c.title}-${i}`}>
                      <em>{v}</em>
                    </div>
                  ) : (
                    <div key={`${c.title}-${i}`}>{v}</div>
                  )
                )
              ) : (
                <span>{value}</span>
              )}
            </Box>
            {c.showDrop && dropItems.length > 1 && (
              <IconButton
                disabled={!size(get(item, c.dropKey, []))}
                onClick={toggleOpen}
                color="primary"
                component="span"
                size="large">
                <Icon>{open ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>
              </IconButton>
            )}
          </FlexGridCell>
        );
      })}
    </FlexGridRow>
  );
};
